<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { wsClient } from '@/net/WebSocketClient'
import { MsgIDs } from '@/defines/MsgIDs'
import { 
  Client2ProxyReqLogin, 
  Client2ProxyAckLogin,
  Client2ProxyReqMobileSmsCode,
  Client2ProxyAckMobileSmsCode
} from '@/proto/proto.js'

const phoneNumber = ref('')
const otpCode = ref('')

const handleSmsAck = (payload: Uint8Array) => {
  const rsp = Client2ProxyAckMobileSmsCode.decode(payload);
  console.log('[Net] Client2ProxyAckMobileSmsCode:', rsp);
  if (rsp.Ret === 0) {
    alert('OTP sent successfully!');
  } else {
    alert(`OTP Error Code: ${rsp.Ret}`);
  }
}

const handleLoginAck = (payload: Uint8Array) => {
  const rsp = Client2ProxyAckLogin.decode(payload);
  console.log('[Net] Client2ProxyAckLogin:', rsp);
  if (rsp.Ret === 0) {
    alert(`Login Success! User: ${rsp.userInfo?.NickName || ''}`);
    // router.push('/home') could happen here
  } else {
    alert(`Login Failed: Error Code ${rsp.Ret}`);
  }
}

onMounted(() => {
  wsClient.connect();
  wsClient.on(MsgIDs.Client2ProxyAckMobileSmsCode, handleSmsAck);
  wsClient.on(MsgIDs.Client2ProxyAckLogin, handleLoginAck);
})

onUnmounted(() => {
  wsClient.off(MsgIDs.Client2ProxyAckMobileSmsCode, handleSmsAck);
  wsClient.off(MsgIDs.Client2ProxyAckLogin, handleLoginAck);
})

const handleGetOtp = () => {
  if (!phoneNumber.value) {
    alert("Please enter a phone number");
    return;
  }
  const req = Client2ProxyReqMobileSmsCode.create({ 
    PhoneNumber: phoneNumber.value,
    Channel: 1
  });
  const payload = Client2ProxyReqMobileSmsCode.encode(req).finish();
  
  if (wsClient.send(MsgIDs.Client2ProxyReqMobileSmsCode, payload)) {
    console.log('[Net] Sending Client2ProxyReqMobileSmsCode for', phoneNumber.value);
  }
}

const handleLogin = () => {
  if (!phoneNumber.value || !otpCode.value) {
    alert("Please enter both phone and OTP");
    return;
  }
  const req = Client2ProxyReqLogin.create({ 
    Origin: 3, // Assuming 3 for password-based or OTP login
    modileNum: phoneNumber.value, 
    Password: otpCode.value,
    platformType: 3, // web
    ClientMark: "web_client"
  });
  const payload = Client2ProxyReqLogin.encode(req).finish();

  if (wsClient.send(MsgIDs.Client2ProxyReqLogin, payload)) {
    console.log('[Net] Sending Client2ProxyReqLogin', { phone: phoneNumber.value, password: otpCode.value });
  }
}
</script>

<template>
  <div class="login-container">
    <!-- Header with Language & Support -->
    <header class="top-header">
      <div class="lang-selector">
        <img src="https://flagcdn.com/w20/gb.png" alt="UK Flag" class="flag-icon" />
        <span>English</span>
        <svg class="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 9l6 6 6-6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="support-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </div>
    </header>

    <!-- Main Content -->
    <main class="login-content">
      <!-- Login Form Card -->
      <div class="form-card">
        <!-- Phone Input -->
        <div class="input-group">
          <div class="country-code">
            <img src="https://flagcdn.com/w20/pk.png" alt="PK Flag" class="flag-icon" />
            <span>+92</span>
          </div>
          <div class="input-divider"></div>
          <input 
            type="tel" 
            v-model="phoneNumber" 
            placeholder="Phone: 03 XXXXXXXXXX" 
            class="custom-input"
          />
        </div>

        <!-- OTP Input -->
        <div class="input-group otp-group">
          <input 
            type="text" 
            v-model="otpCode" 
            placeholder="Enter OTP" 
            class="custom-input"
          />
          <button @click="handleGetOtp" class="btn-get-otp">Get OTP</button>
        </div>

        <!-- Submit Button -->
        <button @click="handleLogin" class="btn-submit">Login or Register</button>
      </div>
    </main>
  </div>
</template>

<style scoped>
.login-container {
  min-height: 100vh;
  width: 100%;
  background-image: url('@/assets/vegas-bg.png');
  background-size: 100% 100%; /* Full screen adaptation */
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

/* Floor styling */
.floor-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 25vh;
  background-image: 
    linear-gradient(rgba(255, 215, 0, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 215, 0, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(500px) rotateX(60deg);
  transform-origin: bottom center;
  z-index: 0;
  opacity: 0.5;
  pointer-events: none;
}

.top-header {
  width: 100%;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  z-index: 10;
}

.lang-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #fff;
  cursor: pointer;
}

.support-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #FFDF73 0%, #B8860B 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  cursor: pointer;
}

.support-icon svg {
  width: 20px;
  height: 20px;
}

.flag-icon {
  width: 20px;
  border-radius: 2px;
}

.dropdown-icon {
  width: 14px;
  height: 14px;
}

.login-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end; /* Push form towards bottom to avoid overlapping bg logo */
  width: 100%;
  max-width: 400px;
  padding: 0 20px 8vh 20px; /* Some bottom padding */
  z-index: 10;
  flex: 1;
}

.form-card {
  width: 100%;
  background: rgba(35, 35, 35, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.input-group {
  display: flex;
  align-items: center;
  background: #2a2a2a;
  border: 1px solid #3d3d3d;
  border-radius: 6px;
  padding: 4px 12px;
  height: 52px;
  transition: border-color 0.3s ease;
}

.input-group:focus-within {
  border-color: #555;
}

.country-code {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 500;
}

.input-divider {
  width: 1px;
  height: 20px;
  background: #4a4a4a;
  margin: 0 12px;
}

.custom-input {
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  width: 100%;
}

.custom-input::placeholder {
  color: #777;
}

.otp-group {
  padding-right: 4px; /* Less padding on right for the button */
}

.btn-get-otp {
  background: #419629;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0 16px;
  height: 40px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
}

.btn-get-otp:hover {
  background: #4db331;
}

.btn-submit {
  width: 100%;
  height: 52px;
  background: #419629;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(65, 150, 41, 0.3);
}

.btn-submit:hover {
  background: #4db331;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(65, 150, 41, 0.4);
}

.btn-submit:active {
  transform: translateY(1px);
}
</style>
